import {inject, Injectable} from '@angular/core';
import {Actions, concatLatestFrom, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, distinctUntilChanged, filter, map, of, switchMap, take, tap} from 'rxjs';
import {DatevWizardClientIdActions} from './datev-wizard-client-id.actions';
import {mappedHttpErrorResponseOperator, RouterActions} from '@adnova/jf-ng-components';
import {Store} from '@ngrx/store';
import {DatevWizardGetFirmaSelectors} from '../../datev-wizard-get-firma/+store/datev-wizard-get-firma.selectors';
import {DatevWizardClientIdSelectors} from './datev-wizard-client-id.selectors';
import {Router} from '@angular/router';
import {DatevWizardCaseSelectionSelectors} from '../../datev-wizard-case-selection/+store/datev-wizard-case-selection.selectors';
import {
  CreateExportConfigErrorDTO,
  DatevIncometaxExportConfigService,
  FullExportConfigDataDTO,
  SelectiveExportConfigDataDTO,
} from '../../../../../openapi/export-openapi';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DatevWizardRetryContextService} from '../../datev-wizard-login/services/datev-wizard-retry-context.service';


@Injectable()
export class DatevWizardClientIdEffects {

  private readonly _snackbar = inject(MatSnackBar);

  private readonly _exportService = inject(DatevIncometaxExportConfigService);

  private readonly _actions$ = inject(Actions);

  private readonly _store = inject(Store);

  private readonly _router = inject(Router);

  private readonly _retryContextService = inject(DatevWizardRetryContextService);

  /**
   * Verarbeitet das Setzen der Client-ID.
   * Prüft die Konfiguration nur wenn nicht bereits loading oder loaded.
   * Navigiert weiter wenn auf der client-id Seite.
   *
   * @trigger setClientId
   * @dispatch checkIfConfigExists (nur wenn nicht loading/loaded), goToExportDate (wenn auf client-id Seite)
   */
  readonly setClientId$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.setClientId),
      concatLatestFrom((action) => [
        this._store.select(DatevWizardClientIdSelectors.isCheckingConfig(action.firmenId, action.clientId)),
        this._store.select(DatevWizardClientIdSelectors.hasCheckedConfig(action.firmenId, action.clientId)),
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig),
      ]),
      concatMap(([action, isLoading, isLoaded, existingExportConfig]) => {
        const actions = [];
        const currentUrl = this._router.url;

        // Nur prüfen wenn nicht bereits loading oder loaded
        if (!isLoading && !isLoaded) {
          actions.push(DatevWizardClientIdActions.checkIfConfigExists({
            firmenId: action.firmenId,
            clientId: action.clientId,
            redirectWhenFinished: action.redirectWhenFinished,
          }));
        } else if (action.redirectWhenFinished) {
          // Navigieren wir weiter zum nächsten Schritt
          if (existingExportConfig) {
            actions.push(DatevWizardClientIdActions.goToConfigExists({
              firmenId: action.firmenId,
              clientId: action.clientId,
            }));
          } else {
            actions.push(DatevWizardClientIdActions.goToExportDate({
              firmenId: action.firmenId,
              clientId: action.clientId,
            }));
          }
        }

        return actions;
      }),
    ),
  );

  /**
   * Synchronisiert die Client-ID aus der Route OHNE Navigation auszulösen.
   * Prüft die Config nur wenn noch nicht geladen, aber triggert KEINE Navigation.
   *
   * @trigger syncClientIdFromRoute
   * @dispatch checkIfConfigExists (nur wenn nicht loading/loaded, OHNE redirectWhenFinished)
   */
  readonly syncClientIdFromRoute$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.syncClientIdFromRoute),
      concatLatestFrom((action) => [
        this._store.select(DatevWizardClientIdSelectors.isCheckingConfig(action.firmenId, action.clientId)),
        this._store.select(DatevWizardClientIdSelectors.hasCheckedConfig(action.firmenId, action.clientId)),
      ]),
      concatMap(([action, isLoading, isLoaded]) => {
        const actions = [];

        // Nur prüfen wenn nicht bereits loading oder loaded
        // WARN: redirectWhenFinished = false um Navigation zu verhindern!
        if (!isLoading && !isLoaded) {
          actions.push(DatevWizardClientIdActions.checkIfConfigExists({
            firmenId: action.firmenId,
            clientId: action.clientId,
            redirectWhenFinished: false,
          }));
        }

        return actions;
      }),
    ),
  );

  readonly setExistingExportConfig$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.setExistingExportConfig),
      filter(({redirectWhenFinished}) => !!redirectWhenFinished),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId)
      ]),
      filter(([, firmenId]) => !!firmenId),
      concatMap(([action, firmenId]) => {
        const actions = [];
        actions.push(DatevWizardClientIdActions.goToConfigExists({
          firmenId: firmenId!,
          clientId: action.existingExportConfig.datevClientId,
        }));
        return actions;
      }),
    ),
  );

  readonly checkIfConfigExists$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.checkIfConfigExists),
      concatLatestFrom(() => [
        this._store.select(DatevWizardCaseSelectionSelectors.selectedCase)
      ]),
      switchMap(([{firmenId, clientId, redirectWhenFinished}, selectedCase]) => {
        // Payload basierend auf selectedCase erstellen
        const payload = selectedCase === 'fall1'
          ? { type: 'FullExportConfigData' as const }
          : { type: 'SelectiveExportConfigData' as const };

        return this._exportService.createExportConfigRequest({
          firmaId: firmenId,
          datevClientId: clientId,
          name: clientId,
          payload,
        }).pipe(
          switchMap((response) => ([
            DatevWizardClientIdActions.checkIfConfigExistsSuccess({
              firmenId,
              clientId: response.datevClientId,
              redirectWhenFinished,
              loginUrl: response.loginUrl,
            }),
          ])),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<CreateExportConfigErrorDTO>(error),
            switchMap(error => {
              switch (error.status) {
                case 409: {
                  const exportConfigId = error.error?.exportConfigId;
                  return [
                    DatevWizardClientIdActions.checkIfConfigExistsWithConflict({
                      firmenId,
                      clientId,
                      exportConfigId,
                    }),
                    DatevWizardClientIdActions.getExistingExportConfig({
                      exportConfigId,
                      redirectWhenFinished,
                    }),
                    DatevWizardClientIdActions.getExistingExportConfigStatus({
                      exportConfigId,
                      redirectWhenFinished,
                    }),
                  ];
                }
                default: {
                  return [
                    DatevWizardClientIdActions.checkIfConfigExistsFailure({
                      firmenId,
                      clientId,
                      error,
                    }),
                  ];
                }
              }
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Verarbeitet die erfolgreiche Konfigurationsprüfung.
   *
   * - Dispatcht eine Navigation-Action, **nur wenn** `redirectWhenFinished === true`.
   * - Ansonsten wird **gar nichts** dispatcht (Stream endet still).
   * - Fall 1 (Natürliche Person): Navigiert zu export-date (Datum muss eingegeben werden)
   * - Fall 2 (Landwirtschaftlicher Betrieb): Erstellt Config direkt und startet DATEV Login
   */
  readonly checkIfConfigExistsSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardClientIdActions.checkIfConfigExistsSuccess),
      filter(({redirectWhenFinished}) => !!redirectWhenFinished),
      concatLatestFrom(() => this._store.select(DatevWizardCaseSelectionSelectors.selectedCase)),
      map(([{firmenId, clientId, loginUrl}, selectedCase]) => {
        if (selectedCase === 'fall1') {
          // Fall 1: Vollständiger Belegexport → Zu export-date navigieren
          return DatevWizardClientIdActions.goToExportDate({firmenId, clientId});
        } else {
          // Fall 2: Selektiver Belegexport → Config wurde bereits mit korrektem Type erstellt
          // Direkt zur DATEV-Login-URL navigieren (ohne zweiten API-Call)
          if (loginUrl) {
            return DatevWizardClientIdActions.connectToDatevSuccess({loginUrl});
          } else {
            // Fallback: Wenn keine loginUrl vorhanden, connectToDatev dispatchen
            return DatevWizardClientIdActions.connectToDatev();
          }
        }
      }),
    ),
  );

  readonly checkIfConfigExistsFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.checkIfConfigExistsFailure),
      tap(({error}) => {
        this._snackbar.open(
          'Unbekannter Fehler',
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          },
        );
      }),
    ),
    {dispatch: false},
  );

  readonly getExistingExportConfig$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfig),
      switchMap(({exportConfigId, redirectWhenFinished}) => this._exportService.getExportConfig(exportConfigId ?? '').pipe(
        switchMap((exportConfigDto) => ([
          DatevWizardClientIdActions.getExistingExportConfigSuccess({
            existingExportConfig: exportConfigDto,
            redirectWhenFinished,
          }),
        ])),
        catchError(error => of(error).pipe(
          mappedHttpErrorResponseOperator(error),
          switchMap(error => [
            DatevWizardClientIdActions.getExistingExportConfigFailure({
              error,
              exportConfigId,
            }),
          ]),
        )),
      )),
    ),
  );

  readonly getExistingExportConfigFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigFailure),
      tap(({error}) => {
        let errorMessage: string;

        switch (error?.status) {
          case 400:
            errorMessage = 'Konfigurations-ID im falschen Format';
            break;
          case 403:
            errorMessage = 'Keine Berechtigung auf diese Konfiguration';
            break;
          case 404:
            errorMessage = 'Angeforderte Konfiguration nicht gefunden';
            break;
          case 500:
            errorMessage = 'Serverfehler beim Laden der Konfiguration';
            break;
          default:
            errorMessage = 'Fehler beim Laden der Konfiguration';
        }

        this._snackbar.open(
          errorMessage,
          undefined,
          {
            duration: 15000,
            panelClass: 'error',
          },
        );
      }),
    ),
    {dispatch: false},
  );

  getExistingExportConfigSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigSuccess),
      switchMap(({existingExportConfig, redirectWhenFinished}) => [
        DatevWizardClientIdActions.setExistingExportConfig({
          existingExportConfig,
          redirectWhenFinished,
        }),
      ]),
    ),
  );

  readonly getExistingExportConfigStatus$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigStatus),
      switchMap(({exportConfigId, redirectWhenFinished}) => this._exportService.getExportConfigStatus(exportConfigId ?? '').pipe(
        switchMap((exportConfigStatusDto) => ([
          DatevWizardClientIdActions.getExistingExportConfigStatusSuccess({
            existingExportConfigStatus: exportConfigStatusDto,
            redirectWhenFinished,
          }),
        ])),
        catchError(error => of(error).pipe(
          mappedHttpErrorResponseOperator(error),
          switchMap(error => [
            DatevWizardClientIdActions.getExistingExportConfigStatusFailure({
              error,
              exportConfigId,
            }),
          ]),
        )),
      )),
    ),
  );

  readonly getExistingExportConfigStatusFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigStatusFailure),
      tap(({error}) => {
        let errorMessage: string;

        switch (error?.status) {
          case 400:
            errorMessage = 'Konfigurations-ID im falschen Format';
            break;
          case 403:
            errorMessage = 'Keine Berechtigung auf diese Konfiguration';
            break;
          case 404:
            errorMessage = 'Status für diese Konfiguration nicht gefunden';
            break;
          case 500:
            errorMessage = 'Serverfehler beim Laden des Verbindungsstatus';
            break;
          default:
            errorMessage = 'Fehler beim Laden des Verbindungsstatus';
        }

        this._snackbar.open(
          errorMessage,
          undefined,
          {
            duration: 15000,
            panelClass: 'error',
          },
        );
      }),
    ),
    {dispatch: false},
  );

  getExistingExportConfigStatusSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigStatusSuccess),
      switchMap(({existingExportConfigStatus, redirectWhenFinished}) => [
        DatevWizardClientIdActions.setExistingExportConfigStatus({
          existingExportConfigStatus,
          redirectWhenFinished,
        }),
      ]),
    ),
  );

  setExistingExportConfigStatus$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.setExistingExportConfigStatus),
      filter(({redirectWhenFinished}) => !!redirectWhenFinished),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId),
        this._store.select(DatevWizardClientIdSelectors.clientId),
      ]),
      filter(([, firmenId, clientId]) => !!firmenId && !!clientId),
      concatMap(([, firmenId, clientId]) => {
        const actions = [];
        actions.push(DatevWizardClientIdActions.goToConfigExists({
          firmenId: firmenId!,
          clientId: clientId!,
        }));
        return actions;
      }),
    ),
  );

  /**
   * Effect für das Herstellen der DATEV-Verbindung (Fall 2).
   * Erstellt eine SelectiveExportConfigDataDTO und initiiert den Login-Prozess.
   * Verwendet clientId als Verbindungsname.
   *
   * @trigger connectToDatev
   * @dispatch connectToDatevSuccess ODER connectToDatevFailure
   */
  readonly connectToDatev$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardClientIdActions.connectToDatev),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId),
        this._store.select(DatevWizardClientIdSelectors.clientId),
      ]),
      // Prüfe ob alle benötigten Daten vorhanden sind
      filter(([, firmenId, clientId]) => {
        if (!firmenId || !clientId) {
          this._snackbar.open('Fehler: Firma oder Client-ID fehlt', undefined, {
            duration: 5000,
            panelClass: 'error',
          });
          return false;
        }
        return true;
      }),
      switchMap(([, firmenId, clientId]) => {
        // Erstelle das Payload-Objekt für Fall 2 (Selektiver Export)
        const payload: SelectiveExportConfigDataDTO = {
          type: 'SelectiveExportConfigData',
        };

        // API-Call zum Erstellen der Export-Konfiguration mit clientId als Name
        return this._exportService.createExportConfigRequest({
          datevClientId: clientId!,
          firmaId: firmenId!,
          name: clientId!,
          payload: payload,
        }).pipe(
          map(response => DatevWizardClientIdActions.connectToDatevSuccess({
            loginUrl: response.loginUrl
          })),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => [
              DatevWizardClientIdActions.connectToDatevFailure({ error })
            ])
          ))
        );
      })
    )
  );

  /**
   * Effect für erfolgreiche Verbindung - navigiert zur DATEV Login-URL.
   * Die redirect_uri ist bereits in der Login-URL enthalten und führt zu config-details.
   *
   * @trigger connectToDatevSuccess
   */
  readonly connectToDatevSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardClientIdActions.connectToDatevSuccess),
      tap(({ loginUrl }) => {
        // Speichere aktuelle URL für Retry bei Fehler
        this._retryContextService.saveRetryUrl(window.location.href);

        // Navigiere zur DATEV Login-URL (externes OAuth-Login)
        window.location.href = loginUrl;
      })
    ),
    { dispatch: false }
  );

  /**
   * Effect für Verbindungsfehler - zeigt Fehlermeldung an.
   *
   * @trigger connectToDatevFailure
   */
  readonly connectToDatevFailure$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardClientIdActions.connectToDatevFailure),
      tap(({ error }) => {
        if (error.status === 409) {
          this._snackbar.open(
            'Eine Konfiguration für diesen Client existiert bereits',
            undefined,
            {
              duration: 5000,
              panelClass: 'error',
            }
          );
        } else {
          this._snackbar.open(
            'Fehler beim Verbinden mit DATEV',
            undefined,
            {
              duration: 5000,
              panelClass: 'error',
            }
          );
        }
      })
    ),
    { dispatch: false }
  );

  /**
   * Navigiert zur export-date Seite (Fall 1).
   * Wartet bis alle Ladevorgänge abgeschlossen sind bevor navigiert wird.
   * Baut URL mit fall und client Parameter.
   */
  goToExportDate$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.goToExportDate),
      switchMap(({firmenId, clientId}) =>
        // Warte bis keine Ladevorgänge mehr laufen
        this._store.select(DatevWizardClientIdSelectors.isAnyLoadingInProgress(firmenId, clientId)).pipe(
          filter(isLoading => !isLoading), // Warte bis isLoading = false
          take(1), // Nimm nur den ersten Wert nachdem Loading fertig ist
          concatLatestFrom(() => this._store.select(DatevWizardCaseSelectionSelectors.selectedCase)),
          map(([, selectedCase]) => {
            const fall = selectedCase; // 'fall1' oder 'fall2'
            const url = `/datev/income-tax-documents/wizard/export-date/inhaber/${firmenId}/fall/${fall}/client/${clientId}`;
            return RouterActions.navigateByUrl({url});
          })
        )
      ),
    ),
  );

  /**
   * Navigiert zur config-exists Seite.
   * Wartet bis alle Ladevorgänge abgeschlossen sind bevor navigiert wird.
   */
  goToConfigExists$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.goToConfigExists),
      switchMap(({firmenId, clientId}) =>
        // Hole erst die exportConfigId aus dem Store
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig).pipe(
          take(1),
          switchMap(existingConfig => {
            const exportConfigId = existingConfig?.id;
            // Warte bis keine Ladevorgänge mehr laufen
            return this._store.select(
              DatevWizardClientIdSelectors.isAnyLoadingInProgress(firmenId, clientId, exportConfigId)
            ).pipe(
              filter(isLoading => !isLoading), // Warte bis isLoading = false
              take(1), // Nimm nur den ersten Wert nachdem Loading fertig ist
              map(() => {
                const url = `/datev/income-tax-documents/wizard/config-exists/inhaber/${firmenId}/client/${clientId}`;
                return RouterActions.navigateByUrl({url});
              })
            );
          })
        )
      ),
    ),
  );

  /**
   * Navigiert zurück zur case-selection Seite.
   *
   * @trigger goBackToCaseSelection
   * @dispatch RouterActions.navigateByUrl
   */
  readonly goBackToCaseSelection$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.goBackToCaseSelection),
      map(({firmenId}) =>
        RouterActions.navigateByUrl({
          url: `/datev/income-tax-documents/wizard/case-selection/inhaber/${firmenId}`,
        })
      )
    )
  );

}
